/**
 * The medication-identity draft: what section 1 of the add-medication form
 * holds while the patient fills it in, and how that becomes the API's
 * `identity` object.
 *
 * The rule this encodes: the combination of field values the patient ends up
 * with is what identifies the pack, and the server does the identifying. A
 * `medicationId` is only ever carried here when a scanned code resolved
 * exactly to one pack — it is never derived from the `packs` a product lookup
 * happens to list, because the patient was never asked to choose one.
 */
import type {
  MedicationCatalogProduct,
  MedicationIdentityInput,
} from "./types";

export type MedicationIdentityDraft = {
  /** How these values were obtained, not how the medication is taken. */
  source: "manual" | "barcode" | "image";
  /**
   * The market whose packs this is. Null when the patient's record does not
   * say: the client never guesses one, and the server resolves it instead.
   */
  countryCode: string | null;
  productName: string;
  brand: string | null;
  doseForm: string;
  strength: string;
  container: string;
  packQuantity: string;
  /** Only ever set by an exact scan resolution. */
  medicationId: string | null;
  /** The raw string the scanner read, kept whether or not it resolved. */
  scannedCode: string | null;
  /** The prescription image this came from, when the OCR path filled it. */
  uploadId: string | null;
};

export function createMedicationIdentityDraft(
  countryCode: string | null,
): MedicationIdentityDraft {
  return {
    source: "manual",
    countryCode,
    productName: "",
    brand: null,
    doseForm: "",
    strength: "",
    container: "",
    packQuantity: "",
    medicationId: null,
    scannedCode: null,
    uploadId: null,
  };
}

function trimmedOrUndefined(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Turns the draft into the `identity` the create request carries, or null when
 * there is no product name to identify anything by.
 *
 * Every optional field is omitted rather than sent blank: the API rejects
 * blank reported text, and an omitted strength is honestly "she did not say",
 * not "the pack has none".
 */
export function buildMedicationIdentityInput(
  draft: MedicationIdentityDraft,
): MedicationIdentityInput | null {
  const productName = draft.productName.trim();
  if (!productName) return null;
  const countryCode = draft.countryCode?.trim().toUpperCase();

  return {
    source: draft.source,
    // Omitted rather than guessed: the server reads the market from the
    // request when the patient's record does not carry one.
    ...(countryCode?.length === 2 && { countryCode }),
    productName,
    ...(trimmedOrUndefined(draft.brand) && {
      brand: draft.brand!.trim(),
    }),
    ...(trimmedOrUndefined(draft.doseForm) && {
      doseForm: draft.doseForm.trim(),
    }),
    ...(trimmedOrUndefined(draft.strength) && {
      strength: draft.strength.trim(),
    }),
    ...(trimmedOrUndefined(draft.container) && {
      container: draft.container.trim(),
    }),
    ...(trimmedOrUndefined(draft.packQuantity) && {
      packQuantity: draft.packQuantity.trim(),
    }),
    // A pack id only ever comes from a scan that resolved exactly, and only
    // while the scanned values still stand.
    ...(draft.source === "barcode" &&
      draft.medicationId && { medicationId: draft.medicationId }),
    ...(draft.source === "barcode" &&
      trimmedOrUndefined(draft.scannedCode) && {
        scannedCode: draft.scannedCode!.trim(),
      }),
    ...(draft.source === "image" &&
      draft.uploadId && { uploadId: draft.uploadId }),
  };
}

export function normalizeProductName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

/**
 * The one product whose variations the identity fields should offer.
 *
 * Only an exact name match settles it. A lone search result is a suggestion,
 * not an answer: "Meno" narrowing to one MENOPUR row would otherwise show that
 * product's strengths while still submitting "Meno" as the product name, which
 * the server resolves packs by and cannot match. She accepts the catalog's
 * name — by tapping the suggestion or by finishing the word — and then the
 * variations appear.
 */
/**
 * The catalog's own spelling for a product the patient picked out of search.
 *
 * Selecting a row is an answer, where typing a few letters is not, so this is
 * deliberately looser than matchTypedProduct: a row named for the legacy
 * directory or a content article ("Gonal-F", "Gonal") resolves to the product
 * the catalog actually holds ("GONAL-F"), and the draft carries that name from
 * then on. Without it the search offers a product, the patient taps it, and
 * the next step reports no pack on file for the thing it just offered.
 *
 * Only ever one candidate: if the search narrowed to several products, the
 * name she picked is not enough to say which, and nothing is adopted.
 */
export function resolveCatalogProductName(
  picked: string,
  products: MedicationCatalogProduct[],
): string | null {
  const normalized = normalizeProductName(picked);
  if (!normalized) return null;

  const exact = products.find(
    (product) => normalizeProductName(product.productName) === normalized,
  );
  if (exact) return exact.productName;

  const prefixed = products.filter((product) =>
    normalizeProductName(product.productName).startsWith(normalized),
  );
  return prefixed.length === 1 ? prefixed[0]!.productName : null;
}

export function matchTypedProduct(
  typed: string,
  products: MedicationCatalogProduct[],
): MedicationCatalogProduct | null {
  const normalized = normalizeProductName(typed);
  if (!normalized) return null;
  return (
    products.find(
      (product) => normalizeProductName(product.productName) === normalized,
    ) ?? null
  );
}

/**
 * The delivery form the medication record carries, read off section 1 rather
 * than asked for again.
 *
 * Section 1 records the dose form and container printed on the pack, in the
 * catalog's own words ("Solution for injection in pre-filled pen"). The
 * medication row stores a much coarser enum. This maps the first onto the
 * second where the words are unambiguous, and returns null rather than guess —
 * a null `form` is "not stated", which is true, whereas a wrong one would
 * change which inventory unit and which injection guidance she is shown.
 *
 * Both the English and French catalog wordings are matched, because the
 * country decides which one section 1 is holding.
 */
export function inferPatientMedicationForm(
  draft: Pick<MedicationIdentityDraft, "doseForm" | "container">,
): string | null {
  const text = `${draft.doseForm} ${draft.container}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // An extraction sometimes reports the enum-ish "pre_filled_pen" rather
    // than prose; treat separators as spaces so one vocabulary covers both.
    .replace(/[_-]+/g, " ");
  if (!text.trim()) return null;

  const has = (...needles: string[]) =>
    needles.some((needle) => text.includes(needle));

  // Order matters: a pen and a syringe are both "solution for injection", so
  // the specific presentations are decided before the generic ones.
  if (has("pen", "stylo")) return "pre_filled_pen";
  if (has("syringe", "seringue")) return "pre_filled_syringe";
  if (has("powder", "poudre", "lyophilis")) return "lyophilized_powder";
  if (has("patch", "dispositif transdermique")) return "transdermal_patch";
  if (has("vaginal gel", "gel vaginal")) return "vaginal_gel";
  if (has("pessary", "suppositor", "ovule", "capsule vaginale")) {
    return "vaginal_suppository";
  }
  if (has("nasal spray", "pulverisation nasale")) return "nasal_spray";
  if (has("cream", "creme")) return "cream";
  if (has("oral solution", "solution buvable")) return "oral_solution";
  if (has("sublingual")) return "sublingual_tablet";
  if (has("capsule", "gelule")) return "capsule";
  if (has("tablet", "comprime")) return "tablet";
  if (has("vial", "flacon", "cartouche", "ampoule")) {
    return "injection_solution";
  }
  if (has("solution for injection", "solution injectable", "injection")) {
    return "injection_solution";
  }
  return null;
}

/**
 * Point the draft at a different product.
 *
 * Everything below the product name describes one particular box: a strength,
 * a container, a pack size, and possibly the code scanned off it. None of that
 * survives a change of product — a Menopur pack's "600 UI" is not one of
 * Gonal-F's strengths, and leaving it selected would submit a combination no
 * pack has. The country is hers and stays; so does an upload, which is the
 * document the whole entry came from.
 *
 * Re-typing the same name changes nothing, so this is safe to call on every
 * keystroke.
 */
export function withProductName(
  draft: MedicationIdentityDraft,
  nextProductName: string,
): MedicationIdentityDraft {
  if (draft.productName === nextProductName) return draft;

  // Naming something that had no name is not changing product — it is the
  // patient telling us what the box she just scanned actually is. A code the
  // catalog could not place leaves the draft nameless, and discarding the
  // code at that moment would throw away the only hard evidence of the pack,
  // which is the opposite of what the screen promises her.
  const wasUnnamed = normalizeProductName(draft.productName) === "";
  const sameProduct =
    wasUnnamed ||
    normalizeProductName(draft.productName) ===
      normalizeProductName(nextProductName);

  return {
    ...draft,
    productName: nextProductName,
    ...(sameProduct
      ? null
      : {
          // She is naming a different medication now, by hand.
          source: draft.source === "image" ? "image" : "manual",
          brand: null,
          doseForm: "",
          strength: "",
          container: "",
          packQuantity: "",
          medicationId: null,
          scannedCode: null,
        }),
  };
}

/** Whether a scanned pack is what filled these fields in. */
export function isFilledFromScan(draft: MedicationIdentityDraft): boolean {
  return draft.source === "barcode" && !!draft.medicationId;
}
